import React, { useState, useEffect } from 'react';
import { 
  Users, Mail, Calendar, MessageSquare, TrendingUp, 
  CheckCircle, Clock, AlertCircle, XCircle, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, activityRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/recent-activity`)
      ]);
      
      setStats(statsRes.data);
      setRecentActivity(activityRes.data.activities || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) {
      return;
    }
    
    setActionLoading(bookingId);
    try {
      await axios.patch(`${API}/bookings/${bookingId}/status?status=cancelled`);
      toast.success('Booking cancelled successfully');
      fetchDashboardData(); // Refresh data
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error('Failed to cancel booking');
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirmBooking = async (bookingId) => {
    setActionLoading(bookingId);
    try {
      await axios.patch(`${API}/bookings/${bookingId}/status?status=confirmed`);
      toast.success('Booking confirmed successfully');
      fetchDashboardData(); // Refresh data
    } catch (error) {
      console.error('Error confirming booking:', error);
      toast.error('Failed to confirm booking');
    } finally {
      setActionLoading(null);
    }
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color }) => (
    <div className={`bg-card border-2 border-${color} rounded-2xl p-6`} style={{ boxShadow: `0 0 15px rgba(${color === 'green' ? '0, 255, 65' : color === 'blue' ? '0, 217, 255' : '255, 0, 255'}, 0.3)` }}>
      <div className="flex items-start justify-between mb-4">
        <div className={`w-12 h-12 bg-${color} rounded-full flex items-center justify-center`}>
          <Icon className="text-inverse" size={24} />
        </div>
        <span className={`text-${color} text-3xl font-black`}>{value}</span>
      </div>
      <h3 className="text-white font-bold text-lg mb-1">{title}</h3>
      <p className="text-secondary text-sm">{subtitle}</p>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-12 flex items-center justify-center">
        <p className="text-white text-xl">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12">
      {/* Hero */}
      <section className="py-12 bg-gradient-to-br from-page via-card to-accent relative">
        <div className="absolute inset-0 cyber-grid opacity-20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="heading-1 text-5xl md:text-6xl font-black uppercase mb-4 leading-tight">
                <span className="text-primary" style={{ textShadow: '0 0 20px var(--neon-green)' }}>Admin </span>
                <span className="text-blue" style={{ textShadow: '0 0 20px var(--neon-blue)' }}>Dashboard</span>
              </h1>
              <p className="text-white">Overview of all activities and submissions</p>
            </div>
            <Button onClick={fetchDashboardData} className="btn-secondary flex items-center gap-2">
              <RefreshCw size={18} />
              Refresh
            </Button>
          </div>
        </div>
      </section>

      {/* Stats Grid */}
      <section className="py-8 bg-page">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <StatCard
              icon={Users}
              title="Total Contacts"
              value={stats?.contacts?.total || 0}
              subtitle={`${stats?.contacts?.new || 0} new • ${stats?.contacts?.recent_week || 0} this week`}
              color="green"
            />
            <StatCard
              icon={Mail}
              title="Newsletter Subs"
              value={stats?.newsletters?.total || 0}
              subtitle={`${stats?.newsletters?.recent_week || 0} new this week`}
              color="blue"
            />
            <StatCard
              icon={Calendar}
              title="Bookings"
              value={stats?.bookings?.total || 0}
              subtitle={`${stats?.bookings?.pending || 0} pending • ${stats?.bookings?.confirmed || 0} confirmed`}
              color="pink"
            />
            <StatCard
              icon={MessageSquare}
              title="Testimonials"
              value={stats?.testimonials?.total || 0}
              subtitle={`${stats?.testimonials?.pending || 0} pending approval`}
              color="green"
            />
          </div>

          {/* Recent Activity */}
          <div className="bg-card border-2 border-blue rounded-2xl p-8" style={{ boxShadow: '0 0 20px rgba(0, 217, 255, 0.3)' }}>
            <h2 className="heading-3 text-3xl font-black text-blue uppercase mb-6 flex items-center gap-2">
              <TrendingUp size={28} />
              Recent Activity
            </h2>
            
            <div className="space-y-4">
              {recentActivity.length === 0 ? (
                <p className="text-secondary text-center py-8">No recent activity</p>
              ) : (
                recentActivity.map((activity, index) => (
                  <div key={index} className="bg-page border-2 border-green rounded-xl p-4 flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                      {activity.type === 'contact' && <Users size={20} className="text-inverse" />}
                      {activity.type === 'booking' && <Calendar size={20} className="text-inverse" />}
                      {activity.type === 'newsletter' && <Mail size={20} className="text-inverse" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between flex-wrap gap-2">
                        <div>
                          <p className="text-white font-semibold">
                            {activity.type === 'contact' && 'New Contact Form'}
                            {activity.type === 'booking' && 'Booking Request'}
                            {activity.type === 'newsletter' && 'Newsletter Signup'}
                          </p>
                          <p className="text-secondary text-sm mt-1">
                            {activity.name || activity.email}
                            {activity.attraction && ` • ${activity.attraction}`}
                            {activity.date && ` • ${activity.date}`}
                            {activity.time && ` @ ${activity.time}`}
                            {activity.guests && ` • ${activity.guests} guest(s)`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {activity.status === 'new' && (
                            <span className="px-2 py-1 bg-blue text-inverse text-xs font-bold rounded-full">NEW</span>
                          )}
                          {activity.status === 'pending' && (
                            <span className="px-2 py-1 bg-pink text-inverse text-xs font-bold rounded-full flex items-center gap-1">
                              <Clock size={12} />
                              PENDING
                            </span>
                          )}
                          {activity.status === 'confirmed' && (
                            <span className="px-2 py-1 bg-primary text-inverse text-xs font-bold rounded-full flex items-center gap-1">
                              <CheckCircle size={12} />
                              CONFIRMED
                            </span>
                          )}
                          {activity.status === 'cancelled' && (
                            <span className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded-full flex items-center gap-1">
                              <XCircle size={12} />
                              CANCELLED
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Action Buttons for Bookings */}
                      {activity.type === 'booking' && activity.status !== 'cancelled' && (
                        <div className="flex gap-2 mt-3">
                          {activity.status === 'pending' && (
                            <Button
                              onClick={() => handleConfirmBooking(activity.id)}
                              disabled={actionLoading === activity.id}
                              className="btn-primary text-xs py-1 px-3 h-auto"
                            >
                              {actionLoading === activity.id ? 'Processing...' : 'Confirm'}
                            </Button>
                          )}
                          <Button
                            onClick={() => handleCancelBooking(activity.id)}
                            disabled={actionLoading === activity.id}
                            variant="destructive"
                            className="text-xs py-1 px-3 h-auto bg-red-600 hover:bg-red-700"
                          >
                            {actionLoading === activity.id ? 'Processing...' : 'Cancel Booking'}
                          </Button>
                        </div>
                      )}
                      
                      <p className="text-secondary text-xs mt-2">
                        {new Date(activity.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <div className="bg-card border-2 border-green rounded-2xl p-6 text-center">
              <AlertCircle className="text-primary mx-auto mb-3" size={32} />
              <h3 className="text-primary font-bold mb-2">{stats?.contacts?.new || 0} New Contacts</h3>
              <p className="text-secondary text-sm">Require attention</p>
            </div>
            <div className="bg-card border-2 border-pink rounded-2xl p-6 text-center">
              <Clock className="text-pink mx-auto mb-3" size={32} />
              <h3 className="text-pink font-bold mb-2">{stats?.bookings?.pending || 0} Pending Bookings</h3>
              <p className="text-secondary text-sm">Awaiting confirmation</p>
            </div>
            <div className="bg-card border-2 border-blue rounded-2xl p-6 text-center">
              <MessageSquare className="text-blue mx-auto mb-3" size={32} />
              <h3 className="text-blue font-bold mb-2">{stats?.testimonials?.pending || 0} Testimonials</h3>
              <p className="text-secondary text-sm">Pending approval</p>
            </div>
          </div>

          {/* Email Notification Info */}
          <div className="bg-card border-2 border-primary rounded-2xl p-6 mt-8" style={{ boxShadow: '0 0 15px rgba(0, 255, 65, 0.3)' }}>
            <h3 className="text-primary font-bold mb-3 flex items-center gap-2">
              <Mail size={20} />
              Email Notifications Active
            </h3>
            <p className="text-secondary text-sm">
              You will receive email notifications at <span className="text-white font-semibold">nbryant@thebr8kroom.com</span> and <span className="text-white font-semibold">aharper@thebr8kroom.com</span> for:
            </p>
            <ul className="text-secondary text-sm mt-2 space-y-1">
              <li>• New booking requests</li>
              <li>• Contact form submissions</li>
              <li>• Newsletter signups</li>
              <li>• New testimonials</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};
